import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { alpha, Box, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, Navigate } from 'react-router';

import { BASE } from 'common/src/routes/BaseRoutes';

import useAuth from 'portal/src/auth/useAuth';
import usePageTitle from 'portal/src/navigation/usePageTitle';

import FormTextInput from '../../components/common/forms/FormTextInput';

// Shown after a WorkOS sign in (e.g. a first Microsoft OAuth login) that
// requires the user to confirm their email with a one-time code.
export default function VerifyEmail() {
  usePageTitle('Verify Email');

  const { isAuthenticated, isPasswordExpired, verifyWorkosEmail } = useAuth();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      code: '',
    },
  });

  if (isAuthenticated()) {
    if (isPasswordExpired()) {
      return <Navigate to={BASE.AUTH.CHANGE_EXPIRED_PASSWORD} replace />;
    }
    return <Navigate to={BASE.ROOT} replace />;
  }

  const submit = async ({ code }) => {
    setLoading(true);
    setError(false);

    if (!(await verifyWorkosEmail(code.trim()))) {
      setError(true);
    }
    setLoading(false);
  };

  return (
    <Box>
      <Stack spacing={2} sx={{ alignItems: 'center', marginTop: 4 }}>
        <Stack
          sx={{
            width: '4.5rem',
            height: '4.5rem',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            borderRadius: '50%',
            backgroundColor: (theme) =>
              alpha(theme.palette.secondary.main, 0.1),
          }}
        >
          <Stack
            sx={{
              width: '3rem',
              height: '3rem',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: (theme) =>
                alpha(theme.palette.secondary.main, 0.1),
            }}
          >
            <MarkEmailReadIcon fontSize="large" color="secondary" />
          </Stack>
        </Stack>
        <Typography variant="h3" sx={{ textAlign: 'center' }}>
          Verify your email
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center' }}>
          We sent a verification code to your email address. Enter it below to
          finish signing in. If you don&apos;t see it, please check your spam
          folder.
        </Typography>
      </Stack>
      <form autoComplete="off" onSubmit={handleSubmit(submit)}>
        <Stack spacing={2} sx={{ alignItems: 'center', marginTop: 4 }}>
          <Box
            sx={{
              width: '20rem',
            }}
          >
            <Controller
              name="code"
              control={control}
              render={({ field }) => {
                delete field.ref;

                return (
                  <FormTextInput
                    name="code"
                    placeholder="Verification code"
                    label="Verification code"
                    inputProps={{ autoComplete: 'one-time-code' }}
                    error={!!errors.code}
                    required
                    {...field}
                  />
                );
              }}
              rules={{
                required: true,
              }}
            />
          </Box>
          {error && (
            <Typography sx={{ textAlign: 'center', color: 'error.main' }}>
              That code is invalid or has expired. Please try again, or go back
              to login to request a new code.
            </Typography>
          )}
          <Button
            disabled={loading}
            type="submit"
            variant="contained"
            disableElevation
          >
            Verify
          </Button>
          <Button
            disabled={loading}
            variant="text"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to={BASE.AUTH.LOGIN}
          >
            Back to login
          </Button>
        </Stack>
      </form>
    </Box>
  );
}
